'use client';

/**
 * WorkspaceBrandHost — invisible client component that applies BOTH the
 * agency-level whitelabel branding AND the active workspace's branding.
 *
 *   Document title:    "<ClientName> — <AgencyName>"  (collapses gracefully)
 *   Sidebar logo:      uses --ws-accent CSS variable
 *   Agency name:       overrides "Optilink AI Command Center" everywhere via
 *                      --agency-name (read by other components)
 *
 * Re-fetches when the pathname changes (cheap, polling-free) so a workspace
 * switch propagates immediately.
 */

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAgencyBranding } from './AgencyBrandingProvider';

export default function WorkspaceBrandHost() {
  const pathname = usePathname();
  const { branding } = useAgencyBranding();
  const [wsName, setWsName] = useState('My Agency');
  const [wsAccent, setWsAccent] = useState('#2563eb');

  useEffect(() => {
    let alive = true;
    fetch('/api/workspaces')
      .then(r => r.json())
      .then(d => {
        if (!alive || !d?.success) return;
        const active = (d.workspaces || []).find(w => w.id === d.activeId);
        if (active) {
          setWsName(active.name || 'My Agency');
          setWsAccent(active.accent || branding.accentColor || '#2563eb');
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [pathname, branding.accentColor]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const base = branding.agencyName || 'Optilink AI Command Center';
    const showsWs = wsName && wsName !== 'My Agency';
    document.title = showsWs ? `${wsName} — ${base}` : base;
    document.documentElement.style.setProperty('--ws-accent', wsAccent);
    document.documentElement.style.setProperty('--agency-accent', branding.accentColor || '#2563eb');
  }, [wsName, wsAccent, branding.agencyName, branding.accentColor]);

  return null;
}
